import { Controller } from "@hotwired/stimulus";

const STATE_EVENT = "contact-copy:changed";

// Connects to data-controller="contact-copy"
export default class extends Controller {
  static targets = [
    "selectWrapper",
    "select",
    "pill",
    "pillText",
    "fields",
    "sameAs",
    "phoneEmailRow",
  ];
  static values = { contactType: String, initialSameAs: String };

  #boundListeners = [];
  #stateHandler = null;
  #fieldSnapshot = {};

  connect() {
    this.#stateHandler = this._handleStateChange.bind(this);
    document.addEventListener(STATE_EVENT, this.#stateHandler);

    const initial = this.initialSameAsValue;
    if (initial) {
      this.sameAsTarget.value = initial;
      this.selectTarget.value = initial;
      const label =
        Array.from(this.selectTarget.options).find((o) => o.value === initial)
          ?.text || "";
      this._showPill(label);
      this._startBinding(initial);
    }

    // After all controllers have connected, sync dropdown and broadcast initial state
    requestAnimationFrame(() => {
      this._syncDropdownOptions();
      this._broadcastState(!!this.initialSameAsValue);
      if (initial) {
        this._saveSnapshot();
        this._copyValuesFromSource(initial);
      }
    });
  }

  disconnect() {
    this._stopBinding();
    document.removeEventListener(STATE_EVENT, this.#stateHandler);
  }

  copy(event) {
    const value = event.target.value;
    if (!value) return;
    this._stopBinding();
    this._saveSnapshot();
    this.sameAsTarget.value = value;
    this._copyValuesFromSource(value);
    this._startBinding(value);
    this._showPill(event.target.options[event.target.selectedIndex].text);
    this._broadcastState(true);
  }

  reset() {
    this._stopBinding();
    this.sameAsTarget.value = "";
    this.selectTarget.value = "";
    this._restoreSnapshot();
    this._enableFields();
    this.pillTarget.classList.add("d-none");
    this.selectWrapperTarget.classList.remove("d-none");
    this._broadcastState(false);
  }

  // ── Event handling ────────────────────────────────────────────────────────

  _handleStateChange({ detail: { contactType, hasSameAs } }) {
    if (contactType === this.contactTypeValue) return;

    // Disable/enable that option in our dropdown
    const option = Array.from(this.selectTarget.options).find(
      (o) => o.value === contactType,
    );
    if (option) option.disabled = hasSameAs;

    // If we are bound to this contact and it just gained a same_as, unlink us
    if (hasSameAs && this.sameAsTarget.value === contactType) {
      this._forceReset();
    }
  }

  _forceReset() {
    this._stopBinding();
    this.sameAsTarget.value = "";
    this.selectTarget.value = "";
    this._restoreSnapshot();
    this._enableFields();
    this.pillTarget.classList.add("d-none");
    this.selectWrapperTarget.classList.remove("d-none");
    // Broadcast so sibling dropdowns re-enable this contact as a selectable source
    this._broadcastState(false);
  }

  _broadcastState(hasSameAs) {
    document.dispatchEvent(
      new CustomEvent(STATE_EVENT, {
        detail: { contactType: this.contactTypeValue, hasSameAs },
      }),
    );
  }

  // On connect, read other controllers' DOM state to pre-disable options
  _syncDropdownOptions() {
    document
      .querySelectorAll("[data-contact-copy-contact-type-value]")
      .forEach((el) => {
        if (el === this.element) return;
        const contactType = el.dataset.contactCopyContactTypeValue;
        const sameAsEl = el.querySelector(
          "[data-contact-copy-target~='sameAs']",
        );
        const hasSameAs = !!sameAsEl?.value;
        const option = Array.from(this.selectTarget.options).find(
          (o) => o.value === contactType,
        );
        if (option) option.disabled = hasSameAs;
      });
  }

  // ── Snapshot ──────────────────────────────────────────────────────────────

  _saveSnapshot() {
    this.#fieldSnapshot = {};
    this.fieldsTarget.querySelectorAll("input").forEach((el) => {
      if (el.name) this.#fieldSnapshot[el.name] = el.value;
    });
  }

  _restoreSnapshot() {
    this.fieldsTarget.querySelectorAll("input").forEach((el) => {
      if (el.name && el.name in this.#fieldSnapshot)
        el.value = this.#fieldSnapshot[el.name];
    });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  _showPill(label) {
    this.pillTextTarget.textContent = label;
    this._disableFields();
    this.selectWrapperTarget.classList.add("d-none");
    this.pillTarget.classList.remove("d-none");
  }

  _disableFields() {
    this.fieldsTarget.querySelectorAll("input").forEach((el) => {
      el.disabled = true;
      el.style.backgroundColor = "#e9ecef";
      el.style.color = "#6c757d";
      el.style.borderColor = "#ced4da";
    });
    this.phoneEmailRowTarget.classList.add("d-none");
  }

  _enableFields() {
    this.fieldsTarget.querySelectorAll("input").forEach((el) => {
      el.disabled = false;
      el.style.backgroundColor = "";
      el.style.color = "";
      el.style.borderColor = "";
    });
    this.phoneEmailRowTarget.classList.remove("d-none");
  }

  // ── Binding ───────────────────────────────────────────────────────────────

  _sourceContainer(sourceType) {
    return sourceType === "user"
      ? this.element.closest("[data-controller~='form-reload']")
      : document.querySelector(
          `[data-contact-copy-contact-type-value="${sourceType}"]`,
        );
  }

  _copyValuesFromSource(sourceType) {
    const container = this._sourceContainer(sourceType);
    if (!container) return;
    this._fieldPairs(sourceType, container).forEach(([srcEl, destEl]) => {
      destEl.value = srcEl.value;
    });
  }

  _startBinding(sourceType) {
    const container = this._sourceContainer(sourceType);
    if (!container) return;
    this._fieldPairs(sourceType, container).forEach(([srcEl, destEl]) => {
      const handler = () => {
        destEl.value = srcEl.value;
      };
      srcEl.addEventListener("input", handler);
      this.#boundListeners.push({ el: srcEl, handler });
    });
  }

  _stopBinding() {
    this.#boundListeners.forEach(({ el, handler }) =>
      el.removeEventListener("input", handler),
    );
    this.#boundListeners = [];
  }

  _fieldPairs(sourceType, container) {
    const fieldNames = ["first_name", "last_name", "phone", "email"];
    return fieldNames.flatMap((field) => {
      const srcEl =
        sourceType === "user"
          ? container.querySelector(`[name="user[${field}]"]`) ||
            container.querySelector(`[name$="[user_attributes][${field}]"]`)
          : container.querySelector(`[data-field="${field}"]`);
      const destEl = this.fieldsTarget.querySelector(`[data-field="${field}"]`);
      return srcEl && destEl ? [[srcEl, destEl]] : [];
    });
  }
}
